import { useEffect, useMemo, useState } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AutoComplete from '@mui/material/Autocomplete';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Alert } from '@mui/material';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import {
  useGetCubeDefinitionDetails,
  useGetCubeMapSourceAttributes,
  useGetCubeMemberMatchFields,
  useGetPullCubeDropdown,
  usePreviewCubeMemberMatches,
} from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { useGetCubeLayerSetLayersDropdown } from 'services/v1/SystemTenant/AROCube/CubeLayerSetService';
import { useGetCubeLayerList } from 'services/v1/SystemTenant/AROCube/CubeLayerService';
import { useGetTimePeriodSetDetail } from 'services/v1/SystemTenant/AROCube/CubeTimePeriodSetService';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import {
  CubeDefinitionRowItem,
  CubeMapDataRuleParams,
  CubeMemberFieldMatch,
  PullCubeDropdownItem,
} from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { CUBE_LAYER_TYPE, CUBE_MAP_DATA_SPREAD_MODE, CUBE_TIME_PERIOD_SIZE_ORDER } from 'constant/CubeConstant';
import { ModalCubeDefinitionParamsProps } from './ModalCubeDefinitionParameter';
import { FormCubeDefinitionMetaDataValues } from './CubeDefinitionSettingPanel';
import { useParams } from 'react-router';
import {
  useMemberMappingDefinition,
  useMemberMappingDefinitions,
  usePreviewMemberMappingDefinition,
} from 'services/v1/SystemTenant/AROCube/MemberMappingDefinitionService';
import { MemberMappingDefinition } from 'types/api/SystemTenant/AROCube/MemberMappingDefinitionTypes';
import { MemberMappingEditor, memberMappingInput } from '../../MemberMappingDefinition/MemberMappingEditor';
import { MemberMatchConditions } from '../../MemberMappingDefinition/MemberMatchConditions';
import { MemberMatchPreview } from '../../MemberMappingDefinition/MemberMatchPreview';

const autoCompleteStyle = {
  width: '100%',
};

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 24px)', sm: 640 },
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
  // This is a flex column; without this, when content exceeds maxHeight the children shrink (flex-shrink: 1)
  // and their content overflows their boxes, overlapping each other instead of letting the modal scroll.
  '& > *': { flexShrink: 0 },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export type FormikMapDataLayerMappingItem = {
  id?: string;
  targetPullCube: AutoCompleteItem | null;
  sourceCubeLayer: AutoCompleteItem | null;
  destinationCubeLayer: AutoCompleteItem | null;
};

export type MapDataFromCubeToCubeParameterPayload = {
  rowItem: CubeDefinitionRowItem;
  mapDataLayerMappings: FormikMapDataLayerMappingItem[];
};

const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AutoCompleteItem) => (
  <Box component='li' sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }} {...props}>
    <span>{option.label}</span>
  </Box>
);

const SPREAD_POLICY_OPTIONS = [
  {
    value: CUBE_MAP_DATA_SPREAD_MODE.DUPLICATE,
    label: 'Duplicate Spread',
    description:
      'Copy the full source value into every destination period it covers. A yearly value of 1,200 shows as 1,200 in each of the 12 months.',
  },
  {
    value: CUBE_MAP_DATA_SPREAD_MODE.PROPORTIONAL,
    label: 'Proportional Allocation',
    description:
      'Divide the source value by the number of destination periods it covers, so the parts add back up to the original. A yearly value of 1,200 becomes 100 in each of the 12 months.',
  },
];

// Larger period => higher rank (YEAR is largest). Returns -1 for unknown/missing types.
const periodSizeRank = (type?: string) => {
  const index = CUBE_TIME_PERIOD_SIZE_ORDER.indexOf(type ?? '');
  return index === -1 ? -1 : CUBE_TIME_PERIOD_SIZE_ORDER.length - index;
};

// Old MAP rules have no memberMatching setting and continue to match complete member names.
const parseRuleParams = (rawRuleTypeParams?: string): CubeMapDataRuleParams => {
  if (!rawRuleTypeParams) return {};
  try {
    const parsed = JSON.parse(rawRuleTypeParams);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const emptyFieldMatch = (): CubeMemberFieldMatch => ({
  sourceFieldId: '',
  destinationFieldId: '',
});

export function ModalMapDataFromCubeToCube(
  props: ModalCubeDefinitionParamsProps<MapDataFromCubeToCubeParameterPayload> & {
    cubeMetaData: FormCubeDefinitionMetaDataValues;
    cubeId?: string;
    subjectId?: string | number;
    initialLayerMappings?: FormikMapDataLayerMappingItem[];
  }
) {
  const {
    onClose,
    visible = false,
    onSubmitParameter,
    cubeDefinitionItem,
    cubeMetaData,
    cubeId,
    subjectId,
    initialLayerMappings,
  } = props;
  const { cubeId: cubeIdParam } = useParams<{ cubeId: string }>();
  const currentCubeId = cubeId ?? cubeIdParam;

  const [selectedCubeId, setSelectedCubeId] = useState<string | null>(null);
  const [layerMappings, setLayerMappings] = useState<FormikMapDataLayerMappingItem[]>([]);
  const [spreadMode, setSpreadMode] = useState<string>(CUBE_MAP_DATA_SPREAD_MODE.DUPLICATE);
  const [memberMatching, setMemberMatching] = useState<'SAME_NAME' | 'FIELDS' | 'DEFINITION'>('SAME_NAME');
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string | null>(null);
  const [createdDefinition, setCreatedDefinition] = useState<MemberMappingDefinition | null>(null);
  const [mappingEditorOpen, setMappingEditorOpen] = useState(false);
  const [sourceAttributeId, setSourceAttributeId] = useState<string | null>(null);
  const [fieldMatches, setFieldMatches] = useState<CubeMemberFieldMatch[]>([emptyFieldMatch()]);
  const [requestedPreviewKey, setRequestedPreviewKey] = useState<string | null>(null);
  const [explicitLayerMappings, setExplicitLayerMappings] = useState(false);
  const legacyInline = memberMatching === 'FIELDS';
  const usesDefinition = memberMatching === 'DEFINITION';
  const matchesFields = legacyInline || usesDefinition;
  const hasLegacyInlineConfig = parseRuleParams(cubeDefinitionItem?.ruleTypeParams).memberMatching === 'FIELDS';
  const memberMatchPreview = usePreviewCubeMemberMatches();
  const definitionPreview = usePreviewMemberMappingDefinition();
  const activePreview = usesDefinition ? definitionPreview : memberMatchPreview;

  const isCubeMetaDataReady = Boolean(cubeMetaData.cubeName && cubeMetaData.timePeriod && cubeMetaData.layerSet);

  const { data: cubeDefinitionDetails } = useGetCubeDefinitionDetails(currentCubeId);

  // Destination cube's period granularity (MONTH/YEAR/...). Resolved from the time-period-set detail keyed
  // by the (reliable) time-period-set id, because cubeMetaData.timePeriod.type is not populated when editing
  // — there the value comes from the cube details, which omits the type. Falls back to it when present.
  const { data: destinationTimePeriodSet } = useGetTimePeriodSetDetail(cubeMetaData.timePeriod?.value?.toString());
  const destinationPeriodType = destinationTimePeriodSet?.data.timePeriod || cubeMetaData.timePeriod?.type;

  // A field mapping can use a different measure and member list, so it must see all source cubes.
  // Keep the legacy same-name candidate filter for existing configurations.
  const { data: pullCubeDropdownOptions, isLoading: isLoadingPullCubeOptions } = useGetPullCubeDropdown({
    attributeId: matchesFields ? undefined : cubeDefinitionItem?.attribute.value,
  });

  const {
    data: sourceCubeDetails,
    isLoading: isLoadingSourceDetails,
    isError: hasSourceDetailsError,
  } = useGetCubeDefinitionDetails(visible ? selectedCubeId ?? undefined : undefined);
  const sourceMemberId = sourceCubeDetails?.data.member?.value;
  const destinationMemberId = cubeMetaData.member?.value;
  const definitions = useMemberMappingDefinitions(
    sourceMemberId,
    destinationMemberId,
    visible && usesDefinition && !!sourceMemberId && !!destinationMemberId
  );
  const definitionDetails = useMemberMappingDefinition(selectedDefinitionId, visible && usesDefinition);
  const definitionCandidate =
    definitionDetails.data?.data ??
    definitions.data?.data.find((definition) => definition.id === selectedDefinitionId) ??
    (createdDefinition?.id === selectedDefinitionId ? createdDefinition : undefined);
  const selectedDefinition =
    definitionCandidate?.sourceMember.value === sourceMemberId &&
    definitionCandidate?.destinationMember.value === destinationMemberId &&
    !definitionDetails.isError
      ? definitionCandidate
      : null;
  const definitionOptions = (definitions.data?.data ?? []).filter(
    (definition) =>
      definition.sourceMember.value === sourceMemberId && definition.destinationMember.value === destinationMemberId
  );
  if (selectedDefinition && !definitionOptions.some((definition) => definition.id === selectedDefinition.id)) {
    definitionOptions.push(selectedDefinition);
  }
  const activeFieldMatches = usesDefinition ? selectedDefinition?.fieldMatches ?? [] : fieldMatches;
  const {
    data: sourceAttributes,
    isLoading: isLoadingSourceAttributes,
    isError: hasSourceAttributesError,
  } = useGetCubeMapSourceAttributes(visible ? selectedCubeId : null);
  const {
    data: sourceFields,
    isLoading: isLoadingSourceFields,
    isError: hasSourceFieldsError,
  } = useGetCubeMemberMatchFields(visible && matchesFields ? sourceCubeDetails?.data.mainMemberSubjectId : undefined);
  const destinationMemberSubjectId =
    cubeMetaData.member?.value === cubeDefinitionDetails?.data.member?.value
      ? cubeDefinitionDetails?.data.mainMemberSubjectId ?? subjectId
      : subjectId;
  const {
    data: destinationFields,
    isLoading: isLoadingDestinationFields,
    isError: hasDestinationFieldsError,
  } = useGetCubeMemberMatchFields(visible && matchesFields ? destinationMemberSubjectId : undefined);

  const sourceAttributeOptions = sourceAttributes?.data ?? [];
  const selectedSourceAttribute = sourceAttributeOptions.find((item) => item.value === sourceAttributeId) ?? null;

  const { data: destinationCubeLayers, isLoading: isLoadingDestinationLayers } = useGetCubeLayerSetLayersDropdown(
    cubeMetaData.layerSet?.value
  );
  const { data: cubeLayers, isLoading: isLoadingLayerTypes, isError: hasLayerTypesError } = useGetCubeLayerList();
  const layerTypes = useMemo(
    () => new Map((cubeLayers?.data ?? []).map((layer) => [layer.id.toString(), layer.layerType.value.toString()])),
    [cubeLayers?.data]
  );

  const pullCubeDropdownOptionsFiltered = useMemo(() => {
    if (!pullCubeDropdownOptions) return [];
    return pullCubeDropdownOptions.data.filter((option) => option.value !== currentCubeId);
  }, [pullCubeDropdownOptions, currentCubeId]);

  // Resolve the selected source cube against the (enriched) dropdown response. Each option carries the
  // cube's layer set id and its layers, so layer-set comparison and source-layer options need no extra calls.
  const selectedCube = useMemo(
    () => pullCubeDropdownOptionsFiltered.find((option) => option.value === selectedCubeId) ?? null,
    [pullCubeDropdownOptionsFiltered, selectedCubeId]
  );

  const sourceLayerOptions = useMemo<AutoCompleteItem[]>(
    () =>
      (selectedCube?.layers ?? [])
        .filter((layer) =>
          matchesFields ? layer.layerType !== CUBE_LAYER_TYPE.BUDGET : layer.layerType !== CUBE_LAYER_TYPE.BASELINE
        )
        .map((layer) => ({
          value: layer.layerId,
          label: layer.layerName,
        })),
    [selectedCube, matchesFields]
  );

  const savedLayerMappings = useMemo(() => {
    if (initialLayerMappings !== undefined) return initialLayerMappings;
    const attributeId = cubeDefinitionItem?.attribute.value;
    if (!attributeId) return undefined;
    return cubeDefinitionDetails?.data?.attributesMapDataLayerMappings?.[attributeId];
  }, [initialLayerMappings, cubeDefinitionDetails?.data, cubeDefinitionItem?.attribute.value]);

  // Initialise the selected source cube and spread mode from the row item (when editing a configured rule).
  useEffect(() => {
    const params = parseRuleParams(cubeDefinitionItem?.ruleTypeParams);
    setSelectedCubeId(cubeDefinitionItem?.targetPullCube?.value ?? null);
    setSpreadMode(params.spreadMode ?? CUBE_MAP_DATA_SPREAD_MODE.DUPLICATE);
    setMemberMatching(
      params.memberMatching === 'FIELDS' || params.memberMatching === 'DEFINITION' ? params.memberMatching : 'SAME_NAME'
    );
    setSelectedDefinitionId(params.memberMappingDefinitionId ?? null);
    setCreatedDefinition(null);
    setMappingEditorOpen(false);
    setRequestedPreviewKey(null);
    setSourceAttributeId(
      cubeDefinitionItem?.targetPullCubeAttribute?.value ?? cubeDefinitionItem?.attribute.value ?? null
    );
    setFieldMatches(
      Array.isArray(params.fieldMatches) && params.fieldMatches.length ? params.fieldMatches : [emptyFieldMatch()]
    );
  }, [cubeDefinitionItem, visible]);

  // Content signature of the data that seeds the mapping rows. Gating the rebuild on this (rather than
  // on the array references) keeps a user's in-progress source-layer picks from being wiped if react-query
  // refetches the dropdowns with identical content while the modal is open.
  const savedLayerMappingSeedSignature = useMemo(
    () =>
      (savedLayerMappings ?? [])
        .map(
          (mapping) =>
            `${mapping.destinationCubeLayer?.value ?? ''}:${mapping.sourceCubeLayer?.value ?? ''}:${
              mapping.targetPullCube?.value ?? ''
            }:${mapping.id ?? ''}`
        )
        .join('|'),
    [savedLayerMappings]
  );
  const layerMappingSeedSignature = useMemo(() => {
    const destinationPart = (destinationCubeLayers?.data ?? [])
      .map((layer) => `${layer.value}:${layerTypes.get(layer.value.toString()) ?? ''}`)
      .join(',');
    return `${destinationPart}__${savedLayerMappingSeedSignature}`;
  }, [destinationCubeLayers?.data, savedLayerMappingSeedSignature, layerTypes]);

  useEffect(() => {
    setExplicitLayerMappings(Boolean(savedLayerMappingSeedSignature));
  }, [savedLayerMappingSeedSignature, visible]);

  // Build one mapping row per destination layer, pre-filling with any previously saved source layer.
  useEffect(() => {
    const destinationLayers = (destinationCubeLayers?.data ?? []).filter(
      (layer) => layerTypes.get(layer.value.toString()) !== CUBE_LAYER_TYPE.BUDGET
    );
    const savedByDestinationLayerId = new Map<string, FormikMapDataLayerMappingItem>();
    savedLayerMappings?.forEach((mapping) => {
      if (mapping.destinationCubeLayer?.value != null) {
        savedByDestinationLayerId.set(mapping.destinationCubeLayer.value.toString(), mapping);
      }
    });

    setLayerMappings(
      destinationLayers.map((destinationCubeLayer) => {
        const saved = savedByDestinationLayerId.get(destinationCubeLayer.value.toString());
        return {
          id: saved?.id,
          targetPullCube: saved?.targetPullCube ?? null,
          sourceCubeLayer:
            (saved?.targetPullCube?.value ?? cubeDefinitionItem?.targetPullCube?.value) ===
            (selectedCubeId ?? cubeDefinitionItem?.targetPullCube?.value)
              ? saved?.sourceCubeLayer ?? null
              : null,
          destinationCubeLayer,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerMappingSeedSignature, visible]);

  const handleOnCloseModal = () => {
    onClose?.();
    setSelectedCubeId(null);
    setLayerMappings([]);
  };

  const handleOnSelectCube = (value: PullCubeDropdownItem | null) => {
    setSelectedCubeId(value?.value ?? null);
    setSourceAttributeId(matchesFields ? null : cubeDefinitionItem?.attribute.value ?? null);
    setFieldMatches((matches) => matches.map((item) => ({ ...item, sourceFieldId: '' })));
    setSelectedDefinitionId(null);
    // Source layers belong to the selected cube, so clear them whenever the cube changes.
    setLayerMappings((prevData) => prevData.map((item) => ({ ...item, sourceCubeLayer: null })));
  };

  const handleOnChangeMatching = (value: 'SAME_NAME' | 'FIELDS' | 'DEFINITION') => {
    setMemberMatching(value);
  };

  const handleOnSelectSourceLayer = (index: number, value: AutoCompleteItem | null) => {
    setLayerMappings((prevData) => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], sourceCubeLayer: value };
      return newData;
    });
  };

  // True while we have a selected cube id (e.g. when editing) but the dropdown response that carries its
  // layer set / layers has not loaded yet.
  const isResolvingSourceCube = Boolean(selectedCubeId) && !selectedCube && isLoadingPullCubeOptions;

  // Different layer sets require explicit mapping. Field matching can also use explicit mapping inside
  // a shared layer set. Compare ids case-insensitively to avoid treating casing differences as a new set.
  const isLayerSetDifferent = useMemo(() => {
    if (!selectedCube) return false;
    const sourceLayerSetId = selectedCube.layerSetId?.toString().trim().toUpperCase();
    const destinationLayerSetId = cubeMetaData.layerSet?.value?.toString().trim().toUpperCase();
    if (!sourceLayerSetId || !destinationLayerSetId) return false;
    return sourceLayerSetId !== destinationLayerSetId;
  }, [selectedCube, cubeMetaData.layerSet?.value]);
  const usesLayerMappings = isLayerSetDifferent || (matchesFields && explicitLayerMappings);
  const getSourceLayersForDestination = (destinationId?: string | number) =>
    sourceLayerOptions.filter(
      (sourceLayer) =>
        layerTypes.get(destinationId?.toString() ?? '') !== CUBE_LAYER_TYPE.ACTUAL ||
        selectedCube?.layers.find((layer) => layer.layerId === sourceLayer.value)?.layerType === CUBE_LAYER_TYPE.ACTUAL
    );
  const hasInvalidLayerMapping = layerMappings.some(
    (mapping) =>
      mapping.sourceCubeLayer &&
      !getSourceLayersForDestination(mapping.destinationCubeLayer?.value).some(
        (layer) => layer.value === mapping.sourceCubeLayer?.value
      )
  );

  // The spread policy is only relevant when the source period is larger (coarser) than the destination
  // period, e.g. pulling YEAR data into a MONTH cube — one source value must be spread across many periods.
  const isSourcePeriodLarger = useMemo(() => {
    if (!selectedCube) return false;
    const sourceRank = periodSizeRank(selectedCube.timePeriodType);
    const destinationRank = periodSizeRank(destinationPeriodType);
    if (sourceRank < 0 || destinationRank < 0) return false;
    return sourceRank > destinationRank;
  }, [selectedCube, destinationPeriodType]);

  // A rule already saved with a spread mode must always expose the policy so it can be changed — even if the
  // live period comparison can't be evaluated yet (period metadata still loading, etc.).
  const hasSavedSpreadMode = useMemo(
    () => parseRuleParams(cubeDefinitionItem?.ruleTypeParams).spreadMode != null,
    [cubeDefinitionItem]
  );
  const isSpreadApplicable = !matchesFields && Boolean(selectedCube) && (isSourcePeriodLarger || hasSavedSpreadMode);

  const fieldsLoading = isLoadingSourceFields || isLoadingDestinationFields || isLoadingSourceDetails;
  const fieldMatchKeys = activeFieldMatches.map((item) => `${item.sourceFieldId}:${item.destinationFieldId}`);
  const hasDuplicateFieldMatches = new Set(fieldMatchKeys).size !== fieldMatchKeys.length;
  const hasCompleteFieldMatches =
    activeFieldMatches.length > 0 &&
    activeFieldMatches.every(
      (item) =>
        sourceFields?.data.some((field) => field.value === item.sourceFieldId) &&
        destinationFields?.data.some((field) => field.value === item.destinationFieldId)
    );
  const fieldConfigurationError =
    matchesFields && selectedCube
      ? hasSourceDetailsError || hasSourceFieldsError || hasDestinationFieldsError
        ? 'Member fields could not be loaded. Reopen the parameter dialog to try again.'
        : cubeMetaData.subjectX?.value || sourceCubeDetails?.data.subjectX?.value
        ? 'Matching member fields supports cubes with one member dimension. Select a source and destination without a second dimension.'
        : destinationPeriodType && selectedCube.timePeriodType && destinationPeriodType !== selectedCube.timePeriodType
        ? 'Matching member fields requires the same time interval in both cubes.'
        : !fieldsLoading && (!sourceFields?.data.length || !destinationFields?.data.length)
        ? 'Both member lists need text or member reference fields. Define those fields before configuring a match.'
        : null
      : null;

  const isFormValid = useMemo(() => {
    if (!selectedCube || isResolvingSourceCube) return false;
    if (!selectedSourceAttribute || isLoadingSourceAttributes) return false;
    if (
      usesLayerMappings &&
      (isLoadingDestinationLayers ||
        isLoadingLayerTypes ||
        hasLayerTypesError ||
        hasInvalidLayerMapping ||
        !layerMappings.some((item) => Boolean(item.sourceCubeLayer)))
    )
      return false;
    if (isSpreadApplicable && !spreadMode) return false;
    if (matchesFields) {
      if (fieldsLoading || fieldConfigurationError || !destinationPeriodType || !selectedCube.timePeriodType)
        return false;
      if (!hasCompleteFieldMatches) return false;
      if (hasDuplicateFieldMatches) return false;
      if (
        usesDefinition &&
        (!selectedDefinition || definitions.isLoading || definitions.isError || definitionDetails.isError)
      )
        return false;
    }
    return true;
  }, [
    selectedCube,
    isResolvingSourceCube,
    selectedSourceAttribute,
    isLoadingSourceAttributes,
    usesLayerMappings,
    isLoadingDestinationLayers,
    isLoadingLayerTypes,
    hasLayerTypesError,
    hasInvalidLayerMapping,
    layerMappings,
    isSpreadApplicable,
    spreadMode,
    matchesFields,
    fieldsLoading,
    fieldConfigurationError,
    destinationPeriodType,
    hasCompleteFieldMatches,
    hasDuplicateFieldMatches,
    usesDefinition,
    selectedDefinition,
    definitions.isLoading,
    definitions.isError,
    definitionDetails.isError,
  ]);

  const previewPayload = {
    sourceCubeId: selectedCube?.value ?? '',
    destinationMemberId: cubeMetaData.member?.value ?? '',
    ruleTypeParams: JSON.stringify({ memberMatching: 'FIELDS', aggregation: 'SUM', fieldMatches }),
  };
  const currentPreviewPayload =
    usesDefinition && selectedDefinition ? memberMappingInput(selectedDefinition) : previewPayload;
  const previewConfigurationKey = JSON.stringify({ payload: currentPreviewPayload, sourceAttributeId, memberMatching });
  const previewMatchesCurrentConfiguration =
    requestedPreviewKey === previewConfigurationKey &&
    JSON.stringify(activePreview.variables) === JSON.stringify(currentPreviewPayload);
  const preview = previewMatchesCurrentConfiguration ? activePreview.data?.data : undefined;
  const previewErrorMessage = activePreview.error?.response?.data?.message;
  const canPreviewMatches =
    matchesFields &&
    Boolean(selectedCube && cubeMetaData.member?.value) &&
    hasCompleteFieldMatches &&
    !hasDuplicateFieldMatches &&
    !fieldsLoading &&
    !fieldConfigurationError &&
    (!usesDefinition || !!selectedDefinition);

  const handleOnSave = () => {
    if (!cubeDefinitionItem || !selectedCube || !selectedSourceAttribute || !isFormValid) return;

    const targetPullCube: DropdownItem = {
      value: selectedCube.value,
      label: selectedCube.label,
    };

    // Field matching also supports explicit mappings inside the same layer set.
    const mapDataLayerMappings = usesLayerMappings
      ? layerMappings.filter((item) => Boolean(item.sourceCubeLayer)).map((item) => ({ ...item, targetPullCube }))
      : [];

    // ruleTypeParams carries the rule config as JSON; spreadMode is included only when spreading applies.
    const ruleParams: CubeMapDataRuleParams = { memberMatching };
    if (isSpreadApplicable && spreadMode) {
      ruleParams.spreadMode = spreadMode;
    }
    if (legacyInline) {
      ruleParams.aggregation = 'SUM';
      ruleParams.fieldMatches = fieldMatches;
    } else if (usesDefinition && selectedDefinition) {
      ruleParams.aggregation = 'SUM';
      ruleParams.memberMappingDefinitionId = selectedDefinition.id;
    }

    const rowItem: CubeDefinitionRowItem = {
      ...cubeDefinitionItem,
      targetPullCube,
      targetPullCubeAttribute: selectedSourceAttribute,
      targetPullCubeLayer: undefined,
      ruleTypeParams: JSON.stringify(ruleParams),
    };

    onSubmitParameter?.({ rowItem, mapDataLayerMappings });
    handleOnCloseModal();
  };

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal} aria-labelledby='map-cube-title'>
        <Stack direction='column' sx={ModalContentStyle}>
          <Stack px={2} justifyContent='space-between' direction='row' alignItems='center'>
            <Typography id='map-cube-title' variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
              Map Data From Cube to Cube
            </Typography>
            <IconButton onClick={handleOnCloseModal} aria-label='Close map parameter'>
              <HighlightOffRoundedIcon sx={closeIconStyle} />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 1 }} />

          <Stack
            px={2}
            sx={{
              bgcolor: '#FBFBFB',
              py: 2,
              borderTop: '1px solid #E3EBF6',
              borderBottom: '1px solid #E3EBF6',
            }}
          >
            <Typography variant='input-label' fontWeight='bold' color='black'>
              Member Matching
            </Typography>
            <RadioGroup
              row
              aria-label='Member matching'
              value={memberMatching}
              onChange={(event) => handleOnChangeMatching(event.target.value as 'SAME_NAME' | 'FIELDS' | 'DEFINITION')}
            >
              <FormControlLabel value='SAME_NAME' control={<Radio size='small' />} label='Same member name' />
              <FormControlLabel
                value='DEFINITION'
                control={<Radio size='small' />}
                label='Use Member Mapping Definition'
              />
              {hasLegacyInlineConfig && (
                <FormControlLabel
                  value='FIELDS'
                  control={<Radio size='small' />}
                  label='Inline field matching (legacy)'
                />
              )}
            </RadioGroup>
            <Typography variant='input-label-gray' mb={2}>
              {matchesFields
                ? 'Sum the selected measure from all source members whose fields match every condition. Parent totals are excluded.'
                : 'Map values between members with the same complete name.'}
            </Typography>
            <Typography variant='input-label' fontWeight='bold' color='black' mb={1}>
              Select Source Cube :
            </Typography>
            <Typography variant='input-label-gray' mb={2}>
              Select the cube you would like to map the data from into {cubeDefinitionItem?.attribute.label}.
            </Typography>
            {!isCubeMetaDataReady && (
              <Stack mb={1}>
                <Alert severity='warning'>
                  <Typography variant='body2'>
                    Please define the cube name, time period, and layer set for the cube before mapping the data.
                  </Typography>
                </Alert>
              </Stack>
            )}
            <AutoComplete
              // @ts-ignore
              onChange={(event, value) => handleOnSelectCube(value as PullCubeDropdownItem)}
              clearIcon={null}
              size='small'
              value={selectedCube}
              disabled={isLoadingPullCubeOptions || !isCubeMetaDataReady}
              getOptionLabel={(option: AutoCompleteItem) => option.label}
              isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) => option.value === value.value}
              options={pullCubeDropdownOptionsFiltered}
              renderOption={renderOption}
              sx={autoCompleteStyle}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Source Cube'
                  name='targetPullCube'
                  placeholder={isLoadingPullCubeOptions ? 'Loading...' : 'Please Select Cube'}
                />
              )}
            />
            {selectedCube && (
              <Stack mt={2}>
                <AutoComplete
                  size='small'
                  value={selectedSourceAttribute}
                  onChange={(_, value) => setSourceAttributeId(value?.value ?? null)}
                  options={sourceAttributeOptions}
                  disabled={isLoadingSourceAttributes}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Source Measure'
                      placeholder={isLoadingSourceAttributes ? 'Loading...' : 'Select a numeric measure'}
                    />
                  )}
                />
                {hasSourceAttributesError && (
                  <Alert severity='error' sx={{ mt: 1 }}>
                    Source measures could not be loaded. Reopen the parameter dialog to try again.
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>

          {matchesFields && selectedCube && (
            <Stack px={2} pt={2} spacing={1.5}>
              <Typography variant='input-label' fontWeight='bold' color='black'>
                Member Mapping Definition
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Source and destination members are matched using the selected Member Mapping Definition.
              </Typography>
              {fieldConfigurationError && <Alert severity='warning'>{fieldConfigurationError}</Alert>}
              {hasDuplicateFieldMatches && <Alert severity='warning'>Each field condition must be different.</Alert>}
              {legacyInline && (
                <>
                  <Alert severity='info'>
                    This cube uses a legacy inline mapping. It remains supported. Save it as a reusable definition to
                    share it with other cube attributes.
                  </Alert>
                  <MemberMatchConditions
                    value={fieldMatches}
                    onChange={setFieldMatches}
                    sourceFields={sourceFields?.data ?? []}
                    destinationFields={destinationFields?.data ?? []}
                    disabled={fieldsLoading}
                  />
                  <Button
                    sx={{ alignSelf: 'flex-start' }}
                    disabled={
                      !hasCompleteFieldMatches || hasDuplicateFieldMatches || !sourceMemberId || !destinationMemberId
                    }
                    onClick={() => setMappingEditorOpen(true)}
                  >
                    Save as reusable mapping
                  </Button>
                </>
              )}
              {usesDefinition && (
                <>
                  <AutoComplete
                    size='small'
                    value={selectedDefinition}
                    options={definitionOptions}
                    loading={definitions.isLoading}
                    getOptionLabel={(definition) => definition.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, definition) => {
                      setSelectedDefinitionId(definition?.id ?? null);
                      setRequestedPreviewKey(null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Member Mapping Definition'
                        placeholder='Select a reusable mapping'
                      />
                    )}
                  />
                  {(definitions.isError || definitionDetails.isError) && (
                    <Alert severity='error'>
                      Member mapping definitions could not be loaded. Reopen the parameter dialog to try again.
                    </Alert>
                  )}
                  {selectedDefinitionId && definitionCandidate && !selectedDefinition && (
                    <Alert severity='warning'>
                      The saved definition uses different member trees. Choose a compatible definition for the current
                      source and destination.
                    </Alert>
                  )}
                  {sourceMemberId &&
                    destinationMemberId &&
                    !definitions.isLoading &&
                    !definitions.isError &&
                    !definitionOptions.length && (
                      <Alert severity='info'>No reusable definitions connect these member trees yet.</Alert>
                    )}
                </>
              )}
              <Alert severity='info'>
                Matching values replace {cubeDefinitionItem?.attribute.label} for each mapped period and layer. A member
                with no matching source rows receives zero.
              </Alert>
              <ButtonLoading
                sx={{ alignSelf: 'flex-start' }}
                loading={activePreview.isLoading && previewMatchesCurrentConfiguration}
                disabled={!canPreviewMatches}
                onClick={() => {
                  setRequestedPreviewKey(previewConfigurationKey);
                  if (usesDefinition && selectedDefinition)
                    definitionPreview.mutate(memberMappingInput(selectedDefinition));
                  else memberMatchPreview.mutate(previewPayload);
                }}
              >
                Preview Member Matches
              </ButtonLoading>
              {previewMatchesCurrentConfiguration && activePreview.isError && (
                <Alert severity='error'>
                  <Typography variant='body2' fontWeight='bold'>
                    Member match preview failed
                  </Typography>
                  {typeof previewErrorMessage === 'string'
                    ? previewErrorMessage
                    : 'Unable to preview member matches. Try again.'}
                </Alert>
              )}
              {preview && <MemberMatchPreview preview={preview} />}
            </Stack>
          )}

          <Stack px={2} pt={2} minHeight={120}>
            {!selectedCube && !isResolvingSourceCube && (
              <Alert severity='info'>
                <Typography variant='body2'>Select a source cube to continue.</Typography>
              </Alert>
            )}

            {isResolvingSourceCube && (
              <Stack height={200}>
                <ActivityIndicator />
              </Stack>
            )}

            {selectedCube && matchesFields && !isLayerSetDifferent && (
              <Stack mb={1.5}>
                <Typography variant='input-label' fontWeight='bold' color='black'>
                  Layer Mapping
                </Typography>
                <RadioGroup
                  aria-label='Layer mapping mode'
                  value={explicitLayerMappings ? 'EXPLICIT' : 'AUTO'}
                  onChange={(event) => setExplicitLayerMappings(event.target.value === 'EXPLICIT')}
                >
                  <FormControlLabel
                    value='AUTO'
                    control={<Radio size='small' />}
                    label='Use matching layers automatically'
                  />
                  <FormControlLabel value='EXPLICIT' control={<Radio size='small' />} label='Choose source layers' />
                </RadioGroup>
              </Stack>
            )}

            {selectedCube && !usesLayerMappings && (
              <Alert severity='info'>
                <Typography variant='body2'>
                  The source cube shares this cube&apos;s layer set. Each layer receives values from the matching source
                  layer. Budget snapshots remain read-only.
                </Typography>
              </Alert>
            )}

            {selectedCube && usesLayerMappings && (
              <>
                <Typography variant='input-label' fontWeight='bold' color='black' mb={1}>
                  Map Layer Set :
                </Typography>
                <Typography variant='input-label-gray' mb={2}>
                  {isLayerSetDifferent ? 'The source cube uses a different layer set. ' : ''}
                  Choose a source for each destination layer you want to update. Unselected destination layers receive
                  no mapped values. Actual destinations accept Actual sources only; Budget snapshots cannot be
                  destinations.
                </Typography>
                {hasLayerTypesError && (
                  <Alert severity='error'>
                    Layer types could not be loaded. Reopen the parameter dialog to try again.
                  </Alert>
                )}

                {(isLoadingDestinationLayers && !destinationCubeLayers) || isLoadingLayerTypes ? (
                  <Stack height={160}>
                    <ActivityIndicator />
                  </Stack>
                ) : layerMappings.length === 0 ? (
                  <Alert severity='warning'>
                    <Typography variant='body2'>This cube has no layers to map.</Typography>
                  </Alert>
                ) : (
                  layerMappings.map((item, index) => (
                    <Stack key={item.destinationCubeLayer?.value ?? index}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent='space-between'
                        gap={1}
                        py={0.5}
                      >
                        <Stack flex={1}>
                          <Typography variant='input-label-gray' mb={0.5}>
                            Destination Layer
                          </Typography>
                          <Typography variant='body2' fontWeight='bold' color='black'>
                            {item.destinationCubeLayer?.label}
                          </Typography>
                        </Stack>
                        <ArrowForwardRoundedIcon
                          sx={{ color: '#98A2AE', mt: 2.5, display: { xs: 'none', sm: 'block' } }}
                          fontSize='small'
                        />
                        <Stack flex={1} minWidth={0}>
                          <Typography variant='input-label-gray' mb={0.5}>
                            Source Layer
                          </Typography>
                          <AutoComplete
                            // @ts-ignore
                            onChange={(event, value) => handleOnSelectSourceLayer(index, value as AutoCompleteItem)}
                            size='small'
                            value={item.sourceCubeLayer}
                            disabled={hasLayerTypesError}
                            getOptionLabel={(option: AutoCompleteItem) => option.label}
                            isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) =>
                              option.value === value.value
                            }
                            options={getSourceLayersForDestination(item.destinationCubeLayer?.value)}
                            renderOption={renderOption}
                            sx={{ width: '100%' }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name='sourceCubeLayer'
                                placeholder='Please Select Layer'
                                label={`Source layer for ${item.destinationCubeLayer?.label ?? 'destination'}`}
                                error={Boolean(
                                  item.sourceCubeLayer &&
                                    !getSourceLayersForDestination(item.destinationCubeLayer?.value).some(
                                      (layer) => layer.value === item.sourceCubeLayer?.value
                                    )
                                )}
                                helperText={
                                  item.sourceCubeLayer &&
                                  !getSourceLayersForDestination(item.destinationCubeLayer?.value).some(
                                    (layer) => layer.value === item.sourceCubeLayer?.value
                                  )
                                    ? 'Choose a supported source layer.'
                                    : undefined
                                }
                              />
                            )}
                          />
                        </Stack>
                      </Stack>
                      {index < layerMappings.length - 1 && <Divider sx={{ my: 0.5 }} />}
                    </Stack>
                  ))
                )}
              </>
            )}
          </Stack>

          {isSpreadApplicable && (
            <Stack px={2} mt={3}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant='input-label' fontWeight='bold' color='black' mb={1}>
                Spread Policy :
              </Typography>
              <Typography variant='input-label-gray' mb={1.5}>
                {isSourcePeriodLarger && destinationPeriodType
                  ? `The source cube uses a larger time period (${selectedCube?.timePeriodType}) than this cube (${destinationPeriodType}). `
                  : ''}
                Choose how each source value should be spread across the destination periods it covers.
              </Typography>
              <RadioGroup value={spreadMode} onChange={(event) => setSpreadMode(event.target.value)}>
                {SPREAD_POLICY_OPTIONS.map((option) => (
                  <Stack
                    key={option.value}
                    sx={{
                      border: '1px solid #E3EBF6',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      mb: 1,
                      bgcolor: spreadMode === option.value ? '#F5F7FF' : '#FFFFFF',
                    }}
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio size='small' />}
                      label={
                        <Typography variant='body2' fontWeight='bold' color='black'>
                          {option.label}
                        </Typography>
                      }
                      sx={{ m: 0, gap: 0.5 }}
                    />
                    <Typography variant='input-label-gray' ml={3.75}>
                      {option.description}
                    </Typography>
                  </Stack>
                ))}
              </RadioGroup>
            </Stack>
          )}

          <Stack px={2}>
            <Divider sx={{ mb: 1, mt: 2 }} />
            <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
              <Button variant='main-table-panel-border' onClick={handleOnCloseModal}>
                Cancel
              </Button>
              <ButtonLoading variant='main-table-panel' disabled={!isFormValid} onClick={() => handleOnSave()}>
                Save Parameter
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
      {mappingEditorOpen && (
        <MemberMappingEditor
          open
          lockMembers
          seed={{
            name: `${sourceCubeDetails?.data.member?.label ?? 'Source'} to ${
              cubeMetaData.member?.label ?? 'Destination'
            }`,
            sourceMemberId: sourceMemberId ?? '',
            destinationMemberId: destinationMemberId ?? '',
            fieldMatches: legacyInline ? fieldMatches : [emptyFieldMatch()],
          }}
          onClose={() => setMappingEditorOpen(false)}
          onSaved={(definition) => {
            setCreatedDefinition(definition);
            setSelectedDefinitionId(definition.id);
            setMemberMatching('DEFINITION');
            setRequestedPreviewKey(null);
            setMappingEditorOpen(false);
          }}
        />
      )}
    </div>
  );
}
